/**
 * Notificacao — a resposta do sistema, no visual do sistema.
 *
 * O QUE ISTO SUBSTITUI
 *   361 chamadas a `alert`, `confirm` e `prompt` em 73 arquivos. São caixas do
 *   NAVEGADOR: nenhum CSS as alcança. Iam continuar feias depois do redesenho
 *   inteiro, porque não são parte da aplicação — são do Chrome.
 *
 *   Pior: **67 delas despejavam a mensagem crua do erro** na caixa. O usuário,
 *   que é advogado, lia "TypeError: Cannot read properties of undefined" e não
 *   tinha o que fazer com aquilo.
 *
 * TRÊS COISAS, TRÊS COMPORTAMENTOS
 *   `avisar`    — o sistema conta o que aconteceu. NÃO bloqueia; o trabalho
 *                 continua. É o substituto do `alert`.
 *   `confirmar` — o sistema pergunta antes de algo irreversível. Bloqueia de
 *                 propósito, e devolve Promise<boolean>. Substitui o `confirm`.
 *   `perguntar` — pede um texto. Substitui o `prompt`.
 *
 * POR QUE `confirmar` DEVOLVE PROMISE
 *   O `confirm` do navegador congela a aba inteira e devolve o booleano na hora.
 *   Nada em JavaScript consegue imitar isso sem congelar junto. Então quem chama
 *   passa a usar `await` — e a função que o contém vira `async`. É a única
 *   diferença de uso, e é o preço de ter uma caixa que obedece ao design.
 *
 * ACESSIBILIDADE
 *   Todo aviso passa por `anunciar()`, senão quem usa leitor de tela não fica
 *   sabendo de nada — o toast aparece num canto e o leitor não o lê sozinho.
 *   A confirmação usa o `Dialogo` do design system (egora), que prende o foco e fecha no Esc.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { anunciar } from './Anuncio';
import { Aviso, Botao, Campo, Dialogo } from '../egora';

// ── canal entre as funções soltas e o componente montado ───────────────────
const ouvintesAviso = new Set();
const ouvintesDialogo = new Set();

// `tom` é o do componente Aviso (egora); `vida` é quanto tempo o aviso fica na tela.
const TONS = {
  sucesso: { tom: 'sucesso', vida: 4000 },
  erro:    { tom: 'perigo',  vida: 9000 },
  aviso:   { tom: 'atencao', vida: 6000 },
  info:    { tom: 'info',    vida: 4500 },
};

/** Quanto tempo um aviso com "Desfazer" fica na tela: é a janela para voltar atrás. */
export const VIDA_COM_DESFAZER = 6000;

/**
 * Traduz uma falha para uma frase que o usuário consegue usar.
 *
 * NUNCA devolve texto de exceção. "Cannot read properties of undefined" não diz
 * nada a um advogado, e ainda revela como o sistema é feito por dentro.
 */
export function mensagemDeErro(erro, oQueFalhou = 'concluir esta ação') {
  const status = erro?.status ?? erro?.response?.status;
  if (status === 401) return 'Sua sessão expirou. Entre de novo para continuar.';
  if (status === 403) return 'Você não tem permissão para isto. Fale com o administrador do escritório.';
  if (status === 404) return 'O item não foi encontrado. Ele pode ter sido excluído por outra pessoa.';
  if (status === 409) return erro?.detail || 'Esta ação conflita com algo que já existe.';
  if (status === 413) return 'O arquivo é grande demais para ser enviado.';
  if (status === 422) return erro?.detail || 'Algum campo está preenchido de um jeito que o sistema não aceita.';
  if (status === 429) return 'Você tentou muitas vezes seguidas. Espere um minuto e tente de novo.';
  if (status >= 500) return 'O sistema falhou ao processar. Se continuar, avise o suporte.';

  // Mensagem que o PRÓPRIO backend escreveu para o usuário: essa serve, e é
  // melhor que qualquer frase genérica — quem a escreveu sabia do caso.
  // As formas listadas são as que este projeto realmente devolve; `error.message`
  // é a do envelope `{success, error}` do R7.
  const doBackend =
    erro?.detail ?? erro?.mensagem ?? erro?.data?.detail ??
    erro?.error?.message ?? erro?.error?.detail ?? erro?.data?.error?.message;
  if (typeof doBackend === 'string' && doBackend.trim() && doBackend.length < 200) {
    return doBackend;
  }

  if (erro?.name === 'TypeError' || /fetch|network|Failed to fetch/i.test(erro?.message || '')) {
    return 'Não foi possível falar com o servidor. Verifique sua conexão e tente de novo.';
  }
  return `Não foi possível ${oQueFalhou}. Tente de novo; se continuar, avise o suporte.`;
}

/**
 * Conta ao usuário o que aconteceu, sem travar o trabalho dele.
 *
 * @param {string} mensagem  Em português comum, dizendo o que houve.
 * @param {'sucesso'|'erro'|'aviso'|'info'} [tom]
 * @param {{ desfazer?: Function }} [opcoes]  `desfazer` põe o botão "Desfazer" no aviso,
 *   que fica na tela por `VIDA_COM_DESFAZER` ms. Quem chama decide o que desfazer quer dizer.
 */
export function avisar(mensagem, tom = 'info', opcoes = {}) {
  const aviso = {
    id: `${Date.now()}-${Math.random()}`,
    mensagem: String(mensagem ?? '').trim() || 'Ação concluída.',
    tom: TONS[tom] ? tom : 'info',
    desfazer: typeof opcoes.desfazer === 'function' ? opcoes.desfazer : null,
  };
  ouvintesAviso.forEach((o) => o(aviso));
  anunciar(aviso.mensagem, tom === 'erro' ? 'assertivo' : 'educado');
  return aviso.id;
}

/** Atalhos, para o lugar de chamada ficar curto e o tom não virar chute. */
avisar.sucesso = (m) => avisar(m, 'sucesso');
avisar.erro = (m) => avisar(m, 'erro');
avisar.aviso = (m) => avisar(m, 'aviso');

/**
 * Pergunta antes de algo irreversível. `await confirmar(...)` → boolean.
 *
 * @param {object|string} opcoes  Só a mensagem, ou `{ titulo, mensagem,
 *   textoConfirmar, textoCancelar, perigo }`.
 */
export function confirmar(opcoes) {
  const o = typeof opcoes === 'string' ? { mensagem: opcoes } : (opcoes || {});
  return new Promise((resolver) => {
    const pedido = {
      id: `${Date.now()}-${Math.random()}`,
      tipo: 'confirmar',
      titulo: o.titulo || 'Confirmar',
      mensagem: o.mensagem || 'Deseja continuar?',
      textoConfirmar: o.textoConfirmar || 'Confirmar',
      textoCancelar: o.textoCancelar || 'Cancelar',
      perigo: o.perigo !== false && /excluir|apagar|remover|cancelar|revogar|deletar|encerrar/i
        .test(`${o.titulo || ''} ${o.mensagem || ''}`),
      resolver,
    };
    if (ouvintesDialogo.size === 0) {
      // A central não está montada (teste, render isolado). Não invente um
      // "sim" — negar é o lado seguro de uma pergunta que ninguém viu.
      resolver(false);
      return;
    }
    ouvintesDialogo.forEach((f) => f(pedido));
  });
}

/** Pede um texto. `await perguntar(...)` → string, ou null se cancelou. */
export function perguntar(opcoes) {
  const o = typeof opcoes === 'string' ? { mensagem: opcoes } : (opcoes || {});
  return new Promise((resolver) => {
    const pedido = {
      id: `${Date.now()}-${Math.random()}`,
      tipo: 'perguntar',
      titulo: o.titulo || 'Informe',
      mensagem: o.mensagem || '',
      valorInicial: o.valorInicial || '',
      textoConfirmar: o.textoConfirmar || 'Confirmar',
      textoCancelar: o.textoCancelar || 'Cancelar',
      resolver,
    };
    if (ouvintesDialogo.size === 0) { resolver(null); return; }
    ouvintesDialogo.forEach((f) => f(pedido));
  });
}

// ── a central: montada UMA vez, no shell da aplicação ──────────────────────
export default function CentralDeNotificacoes() {
  const [avisos, setAvisos] = useState([]);
  const [dialogo, setDialogo] = useState(null);
  const [texto, setTexto] = useState('');
  const filaRef = useRef([]);

  useEffect(() => {
    const ouvinte = (aviso) => {
      setAvisos((atuais) => {
        // Mesma mensagem repetida não empilha: laço de retry viraria muralha.
        // Com "Desfazer" empilha: cada um desfaz uma coisa diferente.
        if (!aviso.desfazer && atuais.some((a) => a.mensagem === aviso.mensagem)) return atuais;
        return [...atuais, aviso];
      });
      const vida = aviso.desfazer ? VIDA_COM_DESFAZER : TONS[aviso.tom].vida;
      setTimeout(() => {
        setAvisos((atuais) => atuais.filter((a) => a.id !== aviso.id));
      }, vida);
    };
    ouvintesAviso.add(ouvinte);
    return () => { ouvintesAviso.delete(ouvinte); };
  }, []);

  useEffect(() => {
    const ouvinte = (pedido) => {
      // Duas perguntas ao mesmo tempo viram fila, não sobreposição.
      setDialogo((atual) => {
        if (atual) { filaRef.current.push(pedido); return atual; }
        setTexto(pedido.valorInicial || '');
        return pedido;
      });
    };
    ouvintesDialogo.add(ouvinte);
    return () => { ouvintesDialogo.delete(ouvinte); };
  }, []);

  const responder = useCallback((resposta) => {
    setDialogo((atual) => {
      atual?.resolver(resposta);
      const proximo = filaRef.current.shift() || null;
      setTexto(proximo?.valorInicial || '');
      return proximo;
    });
  }, []);

  const fechar = useCallback((id) => {
    setAvisos((atuais) => atuais.filter((a) => a.id !== id));
  }, []);

  // Cancelar (ou Esc) responde "não" numa pergunta de sim/não, e "sem texto" numa pergunta de texto.
  const cancelar = () => responder(dialogo?.tipo === 'perguntar' ? null : false);

  return (
    <>
      <div className="sj-avisos sj-avisos--fixo">
        {avisos.map((a) => (
          <Aviso
            key={a.id}
            tom={TONS[a.tom].tom}
            acao={
              <>
                {a.desfazer && (
                  <Botao variante="fantasma" tamanho="sm" onClick={() => { a.desfazer(); fechar(a.id); }}>Desfazer</Botao>
                )}
                <Botao variante="fantasma" tamanho="sm" icone="close" rotulo="Fechar aviso" onClick={() => fechar(a.id)} />
              </>
            }
          >
            {a.mensagem}
          </Aviso>
        ))}
      </div>

      <Dialogo
        aberto={!!dialogo}
        onFechar={cancelar}
        titulo={dialogo?.titulo || ''}
        texto={dialogo?.mensagem}
        acoes={
          <>
            <Botao variante="secundario" onClick={cancelar}>{dialogo?.textoCancelar}</Botao>
            <Botao
              variante={dialogo?.perigo ? 'perigo' : 'primario'}
              onClick={() => responder(dialogo?.tipo === 'perguntar' ? texto : true)}
            >
              {dialogo?.textoConfirmar}
            </Botao>
          </>
        }
      >
        {dialogo?.tipo === 'perguntar' && (
          <Campo rotulo="Resposta">
            {(p) => (
              <input
                {...p}
                className="sj-campo__entrada"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') responder(texto); }}
              />
            )}
          </Campo>
        )}
      </Dialogo>
    </>
  );
}
